import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger } from
'@/shared/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger } from
'@/shared/components/ui/alert-dialog';
import {
  Mail,
  MessageSquare,
  Bell,
  Settings,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Send,
  Key,
  Phone,
  Globe,
  Shield,
  AlertTriangle } from
'lucide-react';
import { toast } from 'sonner';

const API_URL = API_BASE;

const NotificationSettings = () => {const { t } = useTranslation("wms");
  const [channelStatus, setChannelStatus] = useState(null);
  const [pushStatus, setPushStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testForm, setTestForm] = useState({
    channel: 'email',
    recipient: ''
  });

  // Load status
  const loadStatus = async () => {
    try {
      const [channelRes, pushRes] = await Promise.all([
      fetch(`${API_URL}/api/notifications/status`),
      fetch(`${API_URL}/api/push/status`)]
      );

      if (channelRes.ok) {
        const data = await channelRes.json();
        setChannelStatus(data);
      }

      if (pushRes.ok) {
        const data = await pushRes.json();
        setPushStatus(data);
      }
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  // Send test notification
  const sendTestNotification = async () => {
    setIsLoading(true);
    try {
      const endpoint = testForm.channel === 'push' ?
      `${API_URL}/api/push/test` :
      `${API_URL}/api/notifications/test`;

      const body = testForm.channel === 'push' ?
      {} :
      {
        channel: testForm.channel,
        recipient: testForm.recipient,
        title: 'Test Notification',
        message: 'This is a test notification from EzERP Platform.'
      };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (result.success || result.simulated) {
        toast.success('Test notification sent', {
          description: result.simulated ?
          'Note: Channel not configured - notification simulated' :
          `Sent to ${testForm.recipient}`
        });
      } else {
        toast.error('Failed to send test notification', {
          description: result.error
        });
      }
    } catch (error) {
      toast.error('Error sending test notification');
    } finally {
      setIsLoading(false);
      setShowTestDialog(false);
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="notification-settings">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />{t("notificationSettings", "Notification Settings")}

          </h1>
          <p className="text-muted-foreground">{t("configureEmailSms", "Configure email, SMS, and push notification channels")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadStatus}>
            <RefreshCw className="h-4 w-4 mr-2" />{t("refreshStatus", "Refresh Status")}

          </Button>
          <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
            <DialogTrigger asChild>
              <Button>
                <Send className="h-4 w-4 mr-2" />{t("sendTest", "Send Test")}

              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("sendTestNotification", "Send Test Notification")}</DialogTitle>
                <DialogDescription>{t("sendATest", "Send a test notification through a specific channel")}

                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>{t("channel", "Channel")}</Label>
                  <select
                    className="w-full mt-1 p-2 border rounded-md"
                    value={testForm.channel}
                    onChange={(e) => setTestForm({ ...testForm, channel: e.target.value })}>
                    
                    <option value="email">{t("email", "Email")}</option>
                    <option value="sms">{t("sms", "SMS")}</option>
                    <option value="push">{t("pushNotification", "Push Notification")}</option>
                  </select>
                </div>
                {testForm.channel !== 'push' &&
                <div>
                    <Label>{testForm.channel === 'email' ? 'Email Address' : 'Phone Number'}</Label>
                    <Input
                    value={testForm.recipient}
                    onChange={(e) => setTestForm({ ...testForm, recipient: e.target.value })}
                    placeholder={testForm.channel === 'email' ? 'test@example.com' : '+1234567890'} />
                  
                    <p className="text-xs text-muted-foreground mt-1">
                      {testForm.channel === 'sms' && 'Use E.164 format (e.g., +1234567890)'}
                    </p>
                  </div>
                }
                <Button onClick={sendTestNotification} disabled={isLoading} className="w-full">
                  {isLoading ? 'Sending...' : 'Send Test Notification'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Channel Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Email Channel */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("email", "Email")}</CardTitle>
                  <CardDescription>{t("sendgrid", "SendGrid")}</CardDescription>
                </div>
              </div>
              <Badge variant={channelStatus?.email?.enabled ? 'default' : 'secondary'}>
                {channelStatus?.email?.enabled ?
                <><CheckCircle2 className="h-3 w-3 mr-1" />{t("active", "Active")}</> :
                <><XCircle className="h-3 w-3 mr-1" />{t("notConfigured", "Not Configured")}</>
                }
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("provider", "Provider")}</span>
                <span>{t("sendgrid", "SendGrid")}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("sender", "Sender")}</span>
                <span>{channelStatus?.email?.sender || 'Not set'}</span>
              </div>
              {!channelStatus?.email?.enabled &&
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />{t("setSendgridapikeyIn", "Set SENDGRID_API_KEY in backend/.env")}

                </p>
                </div>
              }
            </div>
          </CardContent>
        </Card>

        {/* SMS Channel */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <MessageSquare className="h-5 w-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("sms", "SMS")}</CardTitle>
                  <CardDescription>{t("twilio", "Twilio")}</CardDescription>
                </div>
              </div>
              <Badge variant={channelStatus?.sms?.enabled ? 'default' : 'secondary'}>
                {channelStatus?.sms?.enabled ?
                <><CheckCircle2 className="h-3 w-3 mr-1" />{t("active", "Active")}</> :
                <><XCircle className="h-3 w-3 mr-1" />{t("notConfigured", "Not Configured")}</>
                }
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("provider", "Provider")}</span>
                <span>{t("twilio", "Twilio")}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("phone", "Phone")}</span>
                <span>{channelStatus?.sms?.phone || 'Not set'}</span>
              </div>
              {!channelStatus?.sms?.enabled &&
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <p className="text-xs text-yellow-700 dark:text-yellow-300 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />{t("setTwilioaccountsidTwilioauthtoken", "Set TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_PHONE_NUMBER")}

                </p>
                </div>
              }
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <Bell className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-base">{t("push", "Push")}</CardTitle>
                  <CardDescription>{t("webPush", "Web Push")}</CardDescription>
                </div>
              </div>
              <Badge variant={pushStatus?.enabled ? 'default' : 'secondary'}>
                {pushStatus?.enabled ?
                <><CheckCircle2 className="h-3 w-3 mr-1" />{t("active", "Active")}</> :
                <><XCircle className="h-3 w-3 mr-1" />{t("notConfigured", "Not Configured")}</>
                }
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("vapidKeys", "VAPID Keys")}</span>
                <span>{pushStatus?.vapid_configured ? 'Configured' : 'Not set'}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("subscriptions", "Subscriptions")}</span>
                <span>{pushStatus?.total_subscriptions || 0}{t("devices", "devices")}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{t("active", "Active")}</span>
                <span>{pushStatus?.active_subscriptions || 0}{t("devices", "devices")}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />{t("configurationGuide", "Configuration Guide")}

          </CardTitle>
          <CardDescription>{t("howToConfigure", "How to configure notification channels")}

          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="email">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="email">{t("emailSendgrid", "Email (SendGrid)")}</TabsTrigger>
              <TabsTrigger value="sms">{t("smsTwilio", "SMS (Twilio)")}</TabsTrigger>
              <TabsTrigger value="push">{t("pushNotifications", "Push Notifications")}</TabsTrigger>
            </TabsList>

            <TabsContent value="email" className="mt-4 space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium">{t("1GetSendgrid", "1. Get SendGrid API Key")}</h4>
                <p className="text-sm text-muted-foreground">{t("signUpAt", "Sign up at")}
                  <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{t("sendgridcom", "sendgrid.com")}</a>{t("andCreateAn", "and create an API key with \"Mail Send\" permissions.")}
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">{t("2ConfigureEnvironment", "2. Configure Environment Variables")}</h4>
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-md font-mono text-sm">
                  <p>{t("addToBackendenv", "# Add to backend/.env")}</p>
                  <p>{t("sendgridapikeyyourapikey", "SENDGRID_API_KEY=\"your_api_key\"")}</p>
                  <p>{t("sendgridsenderemailalertsyourdomaincom", "SENDGRID_SENDER_EMAIL=\"alerts@yourdomain.com\"")}</p>
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">{t("3VerifySender", "3. Verify Sender Email")}</h4>
                <p className="text-sm text-muted-foreground">{t("verifyYourSender", "Verify your sender email address in SendGrid's dashboard under Settings \u2192 Sender Authentication.")}

                </p>
              </div>
            </TabsContent>

            <TabsContent value="sms" className="mt-4 space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium">{t("1GetTwilio", "1. Get Twilio Credentials")}</h4>
                <p className="text-sm text-muted-foreground">{t("signUpAt", "Sign up at")}
                  <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{t("twiliocom", "twilio.com")}</a>{t("andGetYour", "and get your Account SID and Auth Token from the console.")}
                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">{t("2GetA", "2. Get a Phone Number")}</h4>
                <p className="text-sm text-muted-foreground">{t("purchaseAPhone", "Purchase a phone number from Twilio that can send SMS messages.")}

                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">{t("3ConfigureEnvironment", "3. Configure Environment Variables")}</h4>
                <div className="bg-slate-100 dark:bg-slate-800 p-3 rounded-md font-mono text-sm">
                  <p>{t("addToBackendenv", "# Add to backend/.env")}</p>
                  <p>{t("twilioaccountsidyouraccountsid", "TWILIO_ACCOUNT_SID=\"your_account_sid\"")}</p>
                  <p>{t("twilioauthtokenyourauthtoken", "TWILIO_AUTH_TOKEN=\"your_auth_token\"")}</p>
                  <p>{t("twiliophonenumber1234567890", "TWILIO_PHONE_NUMBER=\"+1234567890\"")}</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="push" className="mt-4 space-y-4">
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />{t("pushNotificationsReady", "Push Notifications Ready")}

                </h4>
                <p className="text-sm text-muted-foreground">{t("vapidKeysAre", "VAPID keys are already configured. Users can enable push notifications by clicking the bell icon in the header.")}

                </p>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">{t("howItWorks", "How it works:")}</h4>
                <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                  <li>{t("usersClickThe", "Users click the bell icon in the header to enable notifications")}</li>
                  <li>{t("browserPromptsFor", "Browser prompts for permission")}</li>
                  <li>{t("onceGrantedThe", "Once granted, the user receives real-time alerts")}</li>
                  <li>{t("worksInChrome", "Works in Chrome, Firefox, Edge, and Safari (iOS 16.4+)")}</li>
                </ul>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>);

};

export default NotificationSettings;